import { useMemo, useState } from 'react';
import { Save, Users, X } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import {
  deleteRole,
  getPermissionsMatrix,
  listRoles,
  setRolePermissions,
} from '@/api/admin';
import { RoleFormModal } from './RoleFormModal';

const GROUP_COLORS: Record<string, string> = {
  // Keys are the module groups — same groups and order as the sidebar.
  dashboard:          'bg-blue-500/10 text-blue-400 border-blue-500/20',
  procurement:        'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'sales management': 'bg-green-500/10 text-green-400 border-green-500/20',
  'store operations': 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  ecommerce:          'bg-sky-500/10 text-sky-400 border-sky-500/20',
  inventory:          'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'sis management':   'bg-pink-500/10 text-pink-400 border-pink-500/20',
  accounts:           'bg-teal-500/10 text-teal-400 border-teal-500/20',
  reports:            'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  masters:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  system:    'bg-[var(--border-color)] text-[var(--text-muted)] border-[var(--border-color)]',
};

export function RolesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [openId, setOpenId] = useState<number | 'new' | null>(null);
  const [pending, setPending] = useState<Record<string, Record<string, boolean>>>({});
  const [activeRoleCode, setActiveRoleCode] = useState<string | null>(null);

  const rolesQ = useQuery({ queryKey: ['admin-roles'], queryFn: listRoles });
  const matrixQ = useQuery({ queryKey: ['perm-matrix'], queryFn: getPermissionsMatrix });
  const roles = rolesQ.data ?? [];
  const matrix = matrixQ.data;

  const grouped = useMemo(() => {
    if (!matrix) return [];
    const byGroup = new Map<string, typeof matrix.modules>();
    for (const m of matrix.modules) {
      if (!byGroup.has(m.group)) byGroup.set(m.group, []);
      byGroup.get(m.group)!.push(m);
    }
    return Array.from(byGroup.entries());
  }, [matrix]);

  const visibleRoles = useMemo(
    () => (activeRoleCode ? matrix?.roles.filter((r) => r.code === activeRoleCode) ?? [] : matrix?.roles ?? []),
    [matrix, activeRoleCode],
  );

  function effectiveAllowed(roleCode: string, moduleId: string): boolean {
    if (pending[roleCode]?.[moduleId] !== undefined) return pending[roleCode][moduleId];
    return matrix?.modules.find((m) => m.id === moduleId)?.allowedByRole[roleCode] ?? false;
  }

  function toggle(roleCode: string, moduleId: string) {
    if (matrix?.roles.find((r) => r.code === roleCode)?.isSystem) return;
    const cur = effectiveAllowed(roleCode, moduleId);
    setPending((prev) => ({
      ...prev,
      [roleCode]: { ...(prev[roleCode] ?? {}), [moduleId]: !cur },
    }));
  }

  function selectAll(roleCode: string) {
    if (!matrix) return;
    const patches: Record<string, boolean> = {};
    for (const m of matrix.modules) patches[m.id] = true;
    setPending((prev) => ({ ...prev, [roleCode]: patches }));
  }

  function clearAll(roleCode: string) {
    if (!matrix) return;
    const patches: Record<string, boolean> = {};
    for (const m of matrix.modules) patches[m.id] = false;
    setPending((prev) => ({ ...prev, [roleCode]: patches }));
  }

  function toggleGroup(roleCode: string, moduleIds: string[]) {
    const allOn = moduleIds.every((id) => effectiveAllowed(roleCode, id));
    setPending((prev) => ({
      ...prev,
      [roleCode]: {
        ...(prev[roleCode] ?? {}),
        ...Object.fromEntries(moduleIds.map((id) => [id, !allOn])),
      },
    }));
  }

  function accessCount(roleCode: string): number {
    if (!matrix) return 0;
    return matrix.modules.filter((m) => effectiveAllowed(roleCode, m.id)).length;
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      for (const [roleCode, patches] of Object.entries(pending)) {
        const role = roles.find((r) => r.code === roleCode);
        if (!role) continue;
        const arr = Object.entries(patches).map(([moduleId, allowed]) => ({ moduleId, allowed }));
        if (arr.length > 0) await setRolePermissions(role.id, arr);
      }
    },
    onSuccess: () => {
      toast.push('Permissions saved', 'success');
      setPending({});
      qc.invalidateQueries({ queryKey: ['admin-roles'] });
      qc.invalidateQueries({ queryKey: ['perm-matrix'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  const delMut = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      toast.push('Role deleted', 'success');
      qc.invalidateQueries({ queryKey: ['admin-roles'] });
      qc.invalidateQueries({ queryKey: ['perm-matrix'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  const pendingCount = Object.values(pending).reduce((s, m) => s + Object.keys(m).length, 0);

  return (
    <div>
      <SectionHeader
        title="Role Management"        actions={
          pendingCount > 0 ? (
            <div className="flex gap-2">
              <Button variant="glass" size="sm" onClick={() => setPending({})}>
                Discard
              </Button>
              <Button variant="primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                <span className="inline-flex items-center gap-1"><Save size={14} /> Save {pendingCount} change{pendingCount === 1 ? '' : 's'}</span>
              </Button>
            </div>
          ) : null
        }
      />

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={() => setOpenId('new')}
          className="inline-flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs"
        >
          ＋ New Role
        </button>
      </div>

      {/* ── Role cards ── */}
      <div className="mb-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {roles.map((r) => {
          const count = accessCount(r.code);
          const total = matrix?.modules.length ?? 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const isActive = activeRoleCode === r.code;
          return (
            <div
              key={r.id}
              onClick={() => setActiveRoleCode(isActive ? null : r.code)}
              className={`cursor-pointer rounded-xl border p-4 transition-all hover:shadow-md ${
                isActive
                  ? 'border-accent bg-accent/[0.08] shadow-md'
                  : 'border-[var(--border-color)] bg-[var(--card-bg)]'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {r.color && (
                    <span
                      className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ background: r.color }}
                    />
                  )}
                  <div>
                    <div className="text-sm font-extrabold text-[var(--text-primary)]">{r.name}</div>
                  </div>
                </div>
                {r.isSystem ? (
                  <Badge tone="muted">SYSTEM</Badge>
                ) : (
                  <span className="text-[10px] font-bold text-accent">L{r.level}</span>
                )}
              </div>
              {r.description && (
                <div className="mt-2 text-[11px] text-[var(--text-muted)] line-clamp-2">{r.description}</div>
              )}
              {/* Access bar */}
              <div className="mt-3">
                <div className="mb-1 flex justify-between text-[10px] text-[var(--text-muted)]">
                  <span>Module Access</span>
                  <span className="font-bold text-accent">{count}/{total}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-[var(--border-color)]">
                  <div
                    className="h-full rounded-full bg-accent transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--text-muted)]">
                <span><Users size={12} className="mr-1 inline-block align-[-2px]" /> {r.userCount} users</span>
                {!r.isSystem && (
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="glass" size="sm" onClick={() => setOpenId(r.id)}>Edit</Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={async () => {
                        if (await confirmDlg(`Delete role "${r.name}"?`)) delMut.mutate(r.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Permission Matrix ── */}
      {matrix && (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-3">
            <div>
              <span className="text-sm font-extrabold text-[var(--text-primary)]">Permission Matrix</span>
              {activeRoleCode && (
                <button
                  onClick={() => setActiveRoleCode(null)}
                  className="ml-2 text-[10px] font-bold text-accent hover:underline"
                >
                  <X size={12} className="mr-1 inline-block align-[-2px]" /> show all roles
                </button>
              )}
            </div>
            {pendingCount > 0 && (
              <span className="rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-bold text-warning">
                {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '60vh' }}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
                  <th className="sticky left-0 z-30 bg-[var(--bg-secondary)] border-r border-[var(--border-color)] px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                    Module
                  </th>
                  {visibleRoles.map((r) => (
                    <th key={r.code} className="min-w-[110px] px-2 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {r.color && (
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: r.color }} />
                        )}
                        <span className="text-[11px] font-extrabold text-[var(--text-primary)]">{r.name}</span>
                        {!r.isSystem && (
                          <div className="flex gap-1 mt-0.5">
                            <button
                              onClick={() => selectAll(r.code)}
                              className="rounded bg-success/20 px-1.5 py-0.5 text-[9px] font-bold text-success hover:bg-success/30"
                            >
                              All
                            </button>
                            <button
                              onClick={() => clearAll(r.code)}
                              className="rounded bg-danger/20 px-1.5 py-0.5 text-[9px] font-bold text-danger hover:bg-danger/30"
                            >
                              None
                            </button>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(([group, mods]) => {
                  const groupColor = GROUP_COLORS[group] ?? GROUP_COLORS['system'];
                  const moduleIds = mods.map((m) => m.id);
                  return (
                    <>
                      <tr key={`grp-${group}`} className="border-b border-[var(--border-color)]">
                        <td
                          colSpan={visibleRoles.length + 1}
                          className={`px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-wider border-l-2 ${groupColor}`}
                        >
                          {group}
                        </td>
                      </tr>
                      {mods.map((m, idx) => (
                        <tr
                          key={m.id}
                          className={`border-b border-[var(--border-color)]/50 hover:bg-accent/[0.02] ${
                            idx % 2 === 0 ? '' : 'bg-[var(--bg-secondary)]/30'
                          }`}
                        >
                          <td className="sticky left-0 z-10 bg-[var(--bg-secondary)] border-r border-[var(--border-color)] px-4 py-2">
                            <div className="font-semibold text-[var(--text-primary)]">{m.label}</div>
                          </td>
                          {visibleRoles.map((r) => {
                            const allowed = effectiveAllowed(r.code, m.id);
                            const changed = pending[r.code]?.[m.id] !== undefined;
                            const isSystem = r.isSystem;
                            return (
                              <td key={r.code} className={`px-2 py-2 text-center ${changed ? 'bg-warning/5' : ''}`}>
                                <button
                                  disabled={isSystem}
                                  onClick={() => toggle(r.code, m.id)}
                                  title={isSystem ? 'System role — locked' : allowed ? 'Revoke access' : 'Grant access'}
                                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                                    allowed
                                      ? 'border-success bg-success'
                                      : 'border-[var(--border-color)] bg-transparent'
                                  } ${changed ? 'ring-2 ring-warning ring-offset-1' : ''}`}
                                >
                                  {allowed && <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" />}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {/* Group toggle row */}
                      <tr key={`grp-tog-${group}`} className="border-b border-[var(--border-color)]">
                        <td className="sticky left-0 z-10 bg-[var(--bg-secondary)] border-r border-[var(--border-color)] px-4 py-1.5 text-[10px] italic text-[var(--text-muted)]">
                          Select all
                        </td>
                        {visibleRoles.map((r) => {
                          const allOn = moduleIds.every((id) => effectiveAllowed(r.code, id));
                          const someOn = !allOn && moduleIds.some((id) => effectiveAllowed(r.code, id));
                          return (
                            <td key={r.code} className="px-2 py-1 text-center">
                              {!r.isSystem && (
                                <input
                                  type="checkbox"
                                  checked={allOn}
                                  ref={(el) => { if (el) el.indeterminate = someOn; }}
                                  onChange={() => toggleGroup(r.code, moduleIds)}
                                  className="h-3.5 w-3.5 cursor-pointer accent-success"
                                  title={allOn ? 'Deselect all in group' : 'Select all in group'}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pendingCount > 0 && (
            <div className="flex items-center justify-end gap-2 border-t border-[var(--border-color)] px-5 py-3">
              <Button variant="glass" size="sm" onClick={() => setPending({})}>Discard changes</Button>
              <Button variant="primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                <span className="inline-flex items-center gap-1"><Save size={14} /> Save {pendingCount} change{pendingCount === 1 ? '' : 's'}</span>
              </Button>
            </div>
          )}
        </div>
      )}

      <RoleFormModal
        open={openId !== null}
        roleId={openId === 'new' ? null : openId}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}
